import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { makeStyles } from '@material-ui/core/styles';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const MIN_REASON_LENGTH = 50;

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    paddingBottom: theme.spacing(1),
  },
  titleText: {
    fontSize: 18,
    fontWeight: 700,
  },
  repoName: {
    fontFamily: 'monospace',
    backgroundColor: '#F7FAFC',
    padding: '2px 6px',
    borderRadius: 4,
  },
  warningBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
    backgroundColor: '#FFFBEB',
    border: '1px solid #FED7AA',
    borderRadius: 4,
    marginBottom: theme.spacing(3),
  },
  warningIcon: {
    fontSize: 20,
    color: '#D97706',
    marginTop: 2,
  },
  warningText: {
    fontSize: 13,
    color: '#92400E',
    lineHeight: 1.5,
  },
  textField: {
    marginTop: theme.spacing(2),
  },
  charCount: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(0.5),
  },
  charCountText: {
    fontSize: 12,
    color: '#8E9299',
  },
  charCountError: {
    fontSize: 12,
    color: '#E53E3E',
  },
  actions: {
    padding: theme.spacing(2, 3, 3),
  },
  cancelButton: {
    color: '#4A5568',
  },
  excludeButton: {
    backgroundColor: '#E53E3E',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#C53030',
    },
    '&.Mui-disabled': {
      backgroundColor: '#FED7D7',
      color: '#fff',
    },
  },
}));

interface ExcludeRepoDialogProps {
  open: boolean;
  repoName: string;
  onClose: () => void;
  onExclude: (reason: string) => Promise<void>;
  isLoading: boolean;
}

export const ExcludeRepoDialog: React.FC<ExcludeRepoDialogProps> = ({
  open,
  repoName,
  onClose,
  onExclude,
  isLoading,
}) => {
  const classes = useStyles();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const isValid = reason.length >= MIN_REASON_LENGTH;
  const charsRemaining = MIN_REASON_LENGTH - reason.length;

  const handleReasonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setReason(e.target.value);
    if (error) setError(null);
  };

  const handleExclude = async () => {
    if (!isValid) {
      setError(`Reason must be at least ${MIN_REASON_LENGTH} characters`);
      return;
    }

    try {
      await onExclude(reason);
      setReason('');
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to exclude repository');
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setReason('');
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        <Typography className={classes.titleText}>
          Exclude <span className={classes.repoName}>{repoName}</span> from calculations
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box className={classes.warningBox}>
          <WarningAmberIcon className={classes.warningIcon} />
          <Typography className={classes.warningText}>
            Excluding this repository will remove it from coverage summary calculations. 
            The repository will still be visible in the list but marked as excluded.
          </Typography>
        </Box>

        <Typography variant="body2" color="textSecondary">
          Please provide a reason for excluding this repository (minimum {MIN_REASON_LENGTH}{' '}
          characters):
        </Typography>

        <TextField
          className={classes.textField}
          fullWidth
          multiline
          rows={3}
          variant="outlined"
          placeholder="e.g., This repository is used for testing purposes only and should not be included in team coverage metrics..."
          value={reason}
          onChange={handleReasonChange}
          error={!!error || (reason.length > 0 && !isValid)}
          helperText={error}
          disabled={isLoading}
        />

        <Box className={classes.charCount}>
          <Typography className={charsRemaining > 0 ? classes.charCountError : classes.charCountText}>
            {charsRemaining > 0
              ? `${charsRemaining} more characters needed`
              : `${reason.length} characters`}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions className={classes.actions}>
        <Button onClick={handleClose} className={classes.cancelButton} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleExclude}
          className={classes.excludeButton}
          variant="contained"
          disabled={!isValid || isLoading}
          startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {isLoading ? 'Excluding...' : 'Exclude Repository'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExcludeRepoDialog;
